'use client';

import type { CardProps } from '@mui/material/Card';
import Card from '@mui/material/Card';
import { forwardRef } from 'react';

import { tss } from 'tss-react/mui';

const useStyles = tss.create(({ theme }) => {
  return {
    root: {
      borderRadius: '20px',
      transition: 'box-shadow 0.2 ease-in-out',
    },
    outer: {
      border: `1px solid ${theme.cardBorder}`,
      boxShadow: theme.boxShadow,
    },
    innerNavigation: {
      backgroundColor: theme.background,
    },
    inner: {
      border: `1px solid ${theme.cardBorder}`,
    },
    borderedHighlighted: {
      border: `1px solid ${theme.cardBorder}`,
    },
    borderedNone: {
      '&.MuiCard-root': {
        border: 'none',
        boxShadow: 'none',
      },
    },
    borderSquaredTop: {
      borderTopLeftRadius: '0px',
      borderTopRightRadius: '0px',
    },
    borderSquaredBottom: {
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
    },
    borderSquared: {
      borderRadius: '0px',
    },
    selected: {
      boxShadow: theme.boxShadowHighlight,
    },
    hoverHighlight: {
      cursor: 'pointer',
      '&:hover': {
        boxShadow: theme.boxShadowHighlight,
      },
    },

    innerCard: {
      width: '100%',
      display: 'block',
      position: 'relative',
    },
  };
});

export interface Or2CardProps extends CardProps {
  or2Variant?: 'outer' | 'inner' | 'innerNavigation' | 'blank';
  borderedVariant?: 'default' | 'highlighted' | 'none';
  borderRadiusVariant?: 'default' | 'squaredTop' | 'squaredBottom' | 'squared';
  hoverHighlight?: boolean;
  selected?: boolean;
}

export const Or2Card = forwardRef<HTMLDivElement, Or2CardProps>(
  function Or2Card(
    {
      borderedVariant = 'default',
      borderRadiusVariant = 'default',
      or2Variant = 'outer',
      selected = false,
      hoverHighlight = false,
      children,
      className,
      ...other
    },
    ref,
  ) {
    const { classes, cx } = useStyles();

    return (
      <Card
        className={cx(
          {
            [classes.borderedHighlighted]: borderedVariant === 'highlighted',
            [classes.borderedNone]: borderedVariant === 'none',
            [classes.borderSquaredTop]: borderRadiusVariant === 'squaredTop',
            [classes.borderSquaredBottom]:
              borderRadiusVariant === 'squaredBottom',
            [classes.borderSquared]: borderRadiusVariant === 'squared',
            [classes.hoverHighlight]: hoverHighlight,

            [classes.outer]: or2Variant === 'outer',
            [classes.inner]: or2Variant === 'inner',
            [classes.innerNavigation]: or2Variant === 'innerNavigation',
            [classes.selected]: selected,
          },
          className,
        )}
        classes={{ root: classes.root }}
        elevation={0}
        {...other}
        ref={ref}
      >
        <div className={classes.innerCard}>{children}</div>
      </Card>
    );
  },
);
